import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { STATUS } from 'constants/miscConstants';
import AdminService from 'services/AdminService';
import { t } from 'i18next';

const { SUCCESS, ERROR } = STATUS;

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  status: SUCCESS,
  data: { testimonials: [], totalPages: 0, totalCount: 0 },
  filters: {
    pageNumber: 1,
    pageSize: 10,
    search: '',
    active: null,
  },
  submitting: false,
  actionSuccess: false,
  companyOptions: [],
  companyLoading: false,
};

export const getTestimonials = createAsyncThunk('testimonials/getTestimonials', async (data, { rejectWithValue }) => {
  const { queryParams, pageNumber, pageSize, search, active } = data;
  try {
    const response = await AdminService.getTestimonials(queryParams);
    return { response, pageNumber, pageSize, search, active };
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const createTestimonial = createAsyncThunk('testimonials/createTestimonial', async (data, { rejectWithValue }) => {
  try {
    return await AdminService.createTestimonial(data);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to create testimonial');
  }
});

export const updateTestimonial = createAsyncThunk('testimonials/updateTestimonial', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await AdminService.updateTestimonial(id, data);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to update testimonial');
  }
});

export const toggleTestimonialStatus = createAsyncThunk('testimonials/toggleTestimonialStatus', async ({ id, active }, { rejectWithValue }) => {
  try {
    return await AdminService.toggleTestimonialStatus(id, active);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to update status');
  }
});

export const deleteTestimonial = createAsyncThunk('testimonials/deleteTestimonial', async (id, { rejectWithValue }) => {
  try {
    return await AdminService.deleteTestimonial(id);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to delete testimonial');
  }
});

export const searchCompanies = createAsyncThunk('testimonials/searchCompanies', async (search, { rejectWithValue }) => {
  try {
    return await AdminService.searchTestimonialCompanies(search);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to search companies');
  }
});

const setSuccess = (state, message) => {
  state.submitting = false;
  state.status = SUCCESS;
  state.message = message;
  state.showMessage = true;
  state.actionSuccess = true;
};

const setFailure = (state, action) => {
  state.submitting = false;
  state.status = ERROR;
  state.message = action.payload;
  state.showMessage = true;
};

export const testimonialsSlice = createSlice({
  name: 'testimonials',
  initialState,
  reducers: {
    hideMessage: (state) => {
      state.message = '';
      state.status = SUCCESS;
      state.showMessage = false;
      state.actionSuccess = false;
    },
    clearCompanyOptions: (state) => {
      state.companyOptions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTestimonials.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTestimonials.fulfilled, (state, action) => {
        const { response, pageNumber, pageSize, search, active } = action.payload;
        state.data = response || { testimonials: [], totalPages: 0, totalCount: 0 };
        state.filters.pageNumber = pageNumber;
        state.filters.pageSize = pageSize;
        state.filters.search = search;
        state.filters.active = active;
        state.loading = false;
      })
      .addCase(getTestimonials.rejected, (state, action) => {
        state.loading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(createTestimonial.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createTestimonial.fulfilled, (state) => {
        setSuccess(state, t('component.testimonials.message.created'));
      })
      .addCase(createTestimonial.rejected, (state, action) => {
        setFailure(state, action);
      })

      .addCase(updateTestimonial.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateTestimonial.fulfilled, (state) => {
        setSuccess(state, t('component.testimonials.message.updated'));
      })
      .addCase(updateTestimonial.rejected, (state, action) => {
        setFailure(state, action);
      })

      .addCase(toggleTestimonialStatus.fulfilled, (state) => {
        setSuccess(state, t('component.testimonials.message.statusUpdated'));
      })
      .addCase(toggleTestimonialStatus.rejected, (state, action) => {
        setFailure(state, action);
      })

      .addCase(deleteTestimonial.fulfilled, (state) => {
        setSuccess(state, t('component.testimonials.message.deleted'));
      })
      .addCase(deleteTestimonial.rejected, (state, action) => {
        setFailure(state, action);
      })

      .addCase(searchCompanies.pending, (state) => {
        state.companyLoading = true;
      })
      .addCase(searchCompanies.fulfilled, (state, action) => {
        state.companyOptions = action.payload || [];
        state.companyLoading = false;
      })
      .addCase(searchCompanies.rejected, (state) => {
        state.companyLoading = false;
        state.companyOptions = [];
      });
  },
});

export const { hideMessage, clearCompanyOptions } = testimonialsSlice.actions;

export default testimonialsSlice.reducer;
